import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import {
  GraduationCap,
  Layers,
  Loader2,
  MessageSquareWarning,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
  Wand2,
} from "lucide-react";
import { CourseModulesEditor, type ModuleLessonOption } from "@/components/CourseModulesEditor";
import {
  COURSE_INTEREST_TAGS,
  COURSE_LEVELS,
  COURSE_STATUSES,
  COURSE_STATUS_META,
  errorMessage,
  fetchJson,
  interestTagLabel,
  slugifyTitle,
  type Course,
  type CourseStatus,
} from "@/lib/courses";

type AdminCourse = Course & { enrollments: number; completions: number; modules: number };

interface AdminLessonRow {
  id: string;
  title: string;
  slug: string;
  grade: number;
  subject: string;
  topic: string;
  emoji: string | null;
  estimatedMinutes: number;
  xpReward: number;
  status: string;
}

interface AdminEnrollment {
  userId: string;
  firstName: string;
  lastName: string;
  grade: number | null;
  school: string | null;
  enrolledAt: string;
  completedAt: string | null;
  progress: number;
}

// ---------------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------------

const courseFormSchema = z
  .object({
    slug: z
      .string()
      .trim()
      .min(1, "Slug is required")
      .max(160, "Slug is too long")
      .regex(/^[a-z0-9-]+$/, "Use lowercase letters, numbers and dashes only"),
    title: z.string().trim().min(1, "Title is required").max(160, "Title is too long"),
    description: z.string().trim().min(1, "Description is required").max(1000, "Description is too long"),
    emoji: z.string().trim().max(8, "One or two characters is plenty"),
    coverImageUrl: z
      .string()
      .trim()
      .max(500)
      .refine((v) => v === "" || /^https?:\/\//i.test(v), "Must start with http:// or https://"),
    category: z.enum(["school", "interest"]),
    interestTags: z.array(z.string()),
    subject: z.string().trim().max(80),
    gradeMin: z.string().trim().regex(/^$|^\d{1,2}$/, "Whole number or blank"),
    gradeMax: z.string().trim().regex(/^$|^\d{1,2}$/, "Whole number or blank"),
    level: z.enum(["starter", "explorer", "expert"]),
    estimatedMinutes: z.string().trim().regex(/^\d{1,4}$/, "Minutes must be a whole number"),
    credentialTitle: z.string().trim().max(160, "Title is too long"),
    prerequisites: z.array(z.string()),
    sortOrder: z.string().trim().regex(/^-?\d{1,5}$/, "Sort order must be a whole number"),
  })
  .superRefine((v, ctx) => {
    if (v.gradeMin !== "" && v.gradeMax !== "" && Number(v.gradeMin) > Number(v.gradeMax)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["gradeMax"], message: "Highest grade must be at least the lowest grade" });
    }
    if (v.category === "interest" && v.interestTags.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["interestTags"], message: "Pick at least one interest" });
    }
  });

export type CourseFormValues = z.infer<typeof courseFormSchema>;

const EMPTY_FORM: CourseFormValues = {
  slug: "",
  title: "",
  description: "",
  emoji: "",
  coverImageUrl: "",
  category: "interest",
  interestTags: [],
  subject: "",
  gradeMin: "",
  gradeMax: "",
  level: "starter",
  estimatedMinutes: "60",
  credentialTitle: "",
  prerequisites: [],
  sortOrder: "0",
};

function courseToForm(c: AdminCourse): CourseFormValues {
  return {
    slug: c.slug,
    title: c.title,
    description: c.description || "",
    emoji: c.emoji || "",
    coverImageUrl: c.coverImageUrl || "",
    category: c.category,
    interestTags: c.interestTags || [],
    subject: c.subject || "",
    gradeMin: c.gradeMin === null || c.gradeMin === undefined ? "" : String(c.gradeMin),
    gradeMax: c.gradeMax === null || c.gradeMax === undefined ? "" : String(c.gradeMax),
    level: c.level,
    estimatedMinutes: String(c.estimatedMinutes ?? 60),
    credentialTitle: c.credentialTitle || "",
    prerequisites: c.prerequisites || [],
    sortOrder: String(c.sortOrder ?? 0),
  };
}

/**
 * Cleared optional fields are sent as null so an admin can actually blank them out.
 * credentialTitle is omitted instead, so the server keeps its own default.
 */
function formToPayload(v: CourseFormValues) {
  const payload: Record<string, unknown> = {
    slug: v.slug,
    title: v.title,
    description: v.description,
    emoji: v.emoji || null,
    coverImageUrl: v.coverImageUrl || null,
    category: v.category,
    interestTags: v.interestTags,
    subject: v.subject || null,
    gradeMin: v.gradeMin === "" ? null : Number(v.gradeMin),
    gradeMax: v.gradeMax === "" ? null : Number(v.gradeMax),
    level: v.level,
    estimatedMinutes: Number(v.estimatedMinutes),
    prerequisites: v.prerequisites,
    sortOrder: Number(v.sortOrder),
  };
  if (v.credentialTitle) payload.credentialTitle = v.credentialTitle;
  return payload;
}

// ---------------------------------------------------------------------------------
// Small shared bits
// ---------------------------------------------------------------------------------

function StatusBadge({ status }: { status: CourseStatus }) {
  const meta = COURSE_STATUS_META[status] || COURSE_STATUS_META.draft;
  return <Badge className={`border-0 ${meta.className}`}>{meta.label}</Badge>;
}

function TableSkeleton({ rows = 6, cols = 6 }: { rows?: number; cols?: number }) {
  return (
    <div className="space-y-2" data-testid="courses-skeleton">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={`h-8 ${c === 0 ? "flex-[3]" : "flex-1"}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

function TagPicker({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {COURSE_INTEREST_TAGS.map((tag) => {
        const on = value.includes(tag.id);
        return (
          <button
            key={tag.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(on ? value.filter((v) => v !== tag.id) : [...value, tag.id])}
            className={`text-xs rounded-full border px-2.5 py-1 transition-colors disabled:opacity-50 ${
              on ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"
            }`}
            data-testid={`tag-${tag.id}`}
          >
            <span className="mr-1">{tag.emoji}</span>
            {tag.label}
          </button>
        );
      })}
    </div>
  );
}

function PrerequisitePicker({
  value,
  onChange,
  options,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  options: { slug: string; title: string }[];
}) {
  if (options.length === 0) {
    return <p className="text-sm text-muted-foreground">No other courses to require yet.</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
      {options.map((o) => {
        const on = value.includes(o.slug);
        return (
          <button
            key={o.slug}
            type="button"
            onClick={() => onChange(on ? value.filter((v) => v !== o.slug) : [...value, o.slug])}
            className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
              on ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"
            }`}
          >
            {o.title}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------------
// Modules sheet (admin flavour: can add any lesson, any grade)
// ---------------------------------------------------------------------------------

function AdminModulesEditor({ course, onSaved }: { course: AdminCourse; onSaved: () => void }) {
  const { toast } = useToast();
  const [grade, setGrade] = useState("0");

  const {
    data: lessons = [],
    isLoading,
    error,
  } = useQuery<AdminLessonRow[]>({
    queryKey: ["/api/admin/lessons", "course-picker", grade],
    queryFn: () => fetchJson<AdminLessonRow[]>(`/api/admin/lessons?${new URLSearchParams(grade === "all" ? {} : { grade })}`),
  });

  const saveMutation = useMutation({
    mutationFn: async (modules: { lessonId: string; required: boolean }[]) =>
      (await apiRequest("PUT", `/api/admin/courses/${course.id}/modules`, { modules })).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/courses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/courses", course.slug, "modules"] });
      toast({ title: "Lessons saved", description: `${course.title} updated.` });
      onSaved();
    },
    onError: (e: unknown) => toast({ title: "Could not save lessons", description: errorMessage(e), variant: "destructive" }),
  });

  const options: ModuleLessonOption[] = lessons.map((l) => ({
    id: l.id,
    title: l.title,
    slug: l.slug,
    emoji: l.emoji,
    estimatedMinutes: l.estimatedMinutes,
    xpReward: l.xpReward,
    grade: l.grade,
    subject: l.subject,
    topic: l.topic,
    status: l.status,
  }));

  return (
    <CourseModulesEditor
      courseSlug={course.slug}
      courseTitle={course.title}
      lessonOptions={options}
      lessonsLoading={isLoading}
      lessonsError={error}
      extraFilters={
        <Select value={grade} onValueChange={setGrade}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Grade" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Interest (grade 0)</SelectItem>
            <SelectItem value="4">Grade 4</SelectItem>
            <SelectItem value="5">Grade 5</SelectItem>
            <SelectItem value="6">Grade 6</SelectItem>
            <SelectItem value="all">All grades</SelectItem>
          </SelectContent>
        </Select>
      }
      onSave={(modules) => saveMutation.mutate(modules)}
      saving={saveMutation.isPending}
    />
  );
}

// ---------------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------------

export default function AdminCourses() {
  const { toast } = useToast();
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AdminCourse | null>(null);
  const [modulesFor, setModulesFor] = useState<AdminCourse | null>(null);
  const [enrollmentsFor, setEnrollmentsFor] = useState<AdminCourse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminCourse | null>(null);
  const [reviewFor, setReviewFor] = useState<AdminCourse | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  const [genOpen, setGenOpen] = useState(false);
  const [gen, setGen] = useState({ title: "", description: "", interestTags: [] as string[], level: "starter", lessons: "5", audienceNote: "" });
  const [genError, setGenError] = useState<string | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);

  const form = useForm<CourseFormValues>({ resolver: zodResolver(courseFormSchema), defaultValues: EMPTY_FORM });

  const {
    data,
    isLoading,
    error,
  } = useQuery<{ courses: AdminCourse[] }>({
    queryKey: ["/api/admin/courses"],
    queryFn: () => fetchJson<{ courses: AdminCourse[] }>("/api/admin/courses"),
  });
  const courses = data?.courses ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/admin/courses"] });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return courses.filter((c) => {
      if (filterStatus !== "all" && c.status !== filterStatus) return false;
      if (filterCategory !== "all" && c.category !== filterCategory) return false;
      if (!q) return true;
      return [c.title, c.slug, c.subject, c.creatorName, ...(c.interestTags || [])]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [courses, filterStatus, filterCategory, search]);

  const saveMutation = useMutation({
    mutationFn: async (values: CourseFormValues) => {
      const payload = formToPayload(values);
      const res = editing
        ? await apiRequest("PATCH", `/api/admin/courses/${editing.id}`, payload)
        : await apiRequest("POST", "/api/admin/courses", payload);
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      setFormOpen(false);
      setEditing(null);
      toast({ title: editing ? "Course updated" : "Course created" });
    },
    onError: (e: unknown) =>
      toast({ title: editing ? "Could not update the course" : "Could not create the course", description: errorMessage(e), variant: "destructive" }),
  });

  const publishMutation = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: "publish" | "unpublish" }) =>
      (await apiRequest("POST", `/api/admin/courses/${id}/${action}`)).json(),
    onSuccess: (_res, vars) => {
      invalidate();
      toast({ title: vars.action === "publish" ? "Course published" : "Course unpublished" });
    },
    onError: (e: unknown) => toast({ title: "Could not change the status", description: errorMessage(e), variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/admin/courses/${id}`),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
      toast({ title: "Course deleted" });
    },
    onError: (e: unknown) => toast({ title: "Could not delete the course", description: errorMessage(e), variant: "destructive" }),
  });

  const reviewMutation = useMutation({
    mutationFn: async ({ id, note }: { id: string; note: string }) =>
      (await apiRequest("PATCH", `/api/admin/courses/${id}`, { status: "changes_requested", reviewNote: note })).json(),
    onSuccess: () => {
      invalidate();
      setReviewFor(null);
      setReviewNote("");
      toast({ title: "Changes requested", description: "The creator can see your note on their course." });
    },
    onError: (e: unknown) => toast({ title: "Could not send the note", description: errorMessage(e), variant: "destructive" }),
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/courses/generate", {
        title: gen.title,
        description: gen.description || undefined,
        interestTags: gen.interestTags,
        level: gen.level,
        lessons: Number(gen.lessons),
        audienceNote: gen.audienceNote || undefined,
      });
      return res.json();
    },
    onSuccess: (result: { course?: { title?: string }; lessons?: unknown[] }) => {
      invalidate();
      setGenOpen(false);
      setGenError(null);
      setGen({ title: "", description: "", interestTags: [], level: "starter", lessons: "5", audienceNote: "" });
      toast({
        title: "Course drafted",
        description: `${result?.course?.title || "New course"} created with ${result?.lessons?.length ?? 0} draft lessons.`,
      });
    },
    onError: (e: unknown) => setGenError(errorMessage(e, "Generation failed")),
  });

  const openCreate = () => {
    setEditing(null);
    setSlugTouched(false);
    form.reset(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (course: AdminCourse) => {
    setEditing(course);
    setSlugTouched(true);
    form.reset(courseToForm(course));
    setFormOpen(true);
  };

  const category = form.watch("category");
  const prereqOptions = courses
    .filter((c) => c.slug !== editing?.slug)
    .map((c) => ({ slug: c.slug, title: c.title }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-primary" /> Courses
          </h1>
          <p className="text-muted-foreground text-sm">
            Bundles of interactive lessons that award a shareable credential. School courses map to the curriculum, interest
            courses are built around what a student loves.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setGenError(null); setGenOpen(true); }} data-testid="button-generate-course">
            <Wand2 className="w-4 h-4 mr-2" /> Generate with AI
          </Button>
          <Button onClick={openCreate} data-testid="button-new-course">
            <Plus className="w-4 h-4 mr-2" /> New course
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap gap-3">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {COURSE_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {COURSE_STATUS_META[s].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[170px]" data-testid="filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                <SelectItem value="school">School</SelectItem>
                <SelectItem value="interest">Interest</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Search title, slug, subject, creator or interest"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="input-course-search"
              />
            </div>
            <div className="text-sm text-muted-foreground self-center">
              {filtered.length} course{filtered.length === 1 ? "" : "s"}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <TableSkeleton rows={6} cols={6} />
          ) : error ? (
            <div className="text-center py-10 space-y-1">
              <p className="font-medium text-destructive">Could not load courses</p>
              <p className="text-sm text-muted-foreground">{(error as Error)?.message || "Unknown error"}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Course</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Category / Level</TableHead>
                    <TableHead>Lessons</TableHead>
                    <TableHead>Students</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                        {courses.length === 0
                          ? "No courses yet. Create one by hand or let the AI draft a first pass."
                          : "No courses match those filters."}
                      </TableCell>
                    </TableRow>
                  )}
                  {filtered.map((c) => (
                    <TableRow key={c.id} data-testid={`row-course-${c.slug}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{c.emoji || "🎓"}</span>
                          <div className="min-w-0">
                            <p className="font-semibold">{c.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {c.slug}
                              {c.creatorName ? ` · by ${c.creatorName}` : ""}
                            </p>
                            {c.interestTags?.length > 0 && (
                              <p className="text-xs text-muted-foreground">
                                {c.interestTags.map(interestTagLabel).join(", ")}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={c.status} />
                      </TableCell>
                      <TableCell className="text-sm">
                        {c.category === "school" ? "School" : "Interest"}
                        <br />
                        <span className="text-muted-foreground">
                          {c.level}
                          {c.subject ? ` · ${c.subject}` : ""}
                          {c.gradeMin !== null && c.gradeMax !== null ? ` · G${c.gradeMin} to G${c.gradeMax}` : ""}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">
                        {c.modules} lesson{c.modules === 1 ? "" : "s"}
                        <br />
                        <span className="text-muted-foreground">
                          {c.estimatedMinutes} min · {c.xpTotal} XP
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">
                        <span className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" /> {c.enrollments} enrolled
                        </span>
                        <span className="text-muted-foreground">{c.completions} completed</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" title="Lessons" onClick={() => setModulesFor(c)} data-testid={`button-modules-${c.slug}`}>
                            <Layers className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" title="Enrolments" onClick={() => setEnrollmentsFor(c)} data-testid={`button-enrollments-${c.slug}`}>
                            <Users className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" title="Edit" onClick={() => openEdit(c)} data-testid={`button-edit-${c.slug}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {c.status === "pending_review" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setReviewNote(c.reviewNote || ""); setReviewFor(c); }}
                              data-testid={`button-request-changes-${c.slug}`}
                            >
                              <MessageSquareWarning className="w-4 h-4 mr-1" /> Request changes
                            </Button>
                          )}
                          {c.status === "published" ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => publishMutation.mutate({ id: c.id, action: "unpublish" })}
                              disabled={publishMutation.isPending}
                            >
                              Unpublish
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => publishMutation.mutate({ id: c.id, action: "publish" })}
                              disabled={publishMutation.isPending}
                              data-testid={`button-publish-${c.slug}`}
                            >
                              Publish
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-destructive" title="Delete" onClick={() => setDeleteTarget(c)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / edit ------------------------------------------------------------ */}
      <Dialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.title}` : "New course"}</DialogTitle>
            <DialogDescription>
              A course is an ordered set of interactive lessons. Students earn a credential once they finish the required
              lessons.
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form className="space-y-4" onSubmit={form.handleSubmit((values) => saveMutation.mutate(values))}>
              <div className="grid md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Build Your First Robot"
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            if (!slugTouched) form.setValue("slug", slugifyTitle(e.target.value), { shouldValidate: true });
                          }}
                          data-testid="input-title"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="slug"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Slug</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="build-your-first-robot"
                          {...field}
                          onChange={(e) => {
                            setSlugTouched(true);
                            field.onChange(e);
                          }}
                          data-testid="input-slug"
                        />
                      </FormControl>
                      <FormDescription>Used in the course URL. Auto filled from the title, edit it if you like.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea rows={3} placeholder="What will a student be able to do at the end?" {...field} data-testid="input-description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="emoji"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Emoji</FormLabel>
                      <FormControl>
                        <Input placeholder="🤖" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="coverImageUrl"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Cover image URL</FormLabel>
                      <FormControl>
                        <Input placeholder="https://..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger data-testid="select-category">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="school">School (curriculum)</SelectItem>
                          <SelectItem value="interest">Interest</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="level"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Level</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger data-testid="select-level">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {COURSE_LEVELS.map((l) => (
                            <SelectItem key={l.id} value={l.id}>
                              {l.label} · {l.hint}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="interestTags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Interests</FormLabel>
                    <FormControl>
                      <TagPicker value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormDescription>
                      Drives recommendations. {category === "interest" ? "Pick at least one." : "Optional for school courses."}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Subject</FormLabel>
                      <FormControl>
                        <Input placeholder="Mathematics" {...field} />
                      </FormControl>
                      <FormDescription>Optional. School courses only.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="gradeMin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lowest grade</FormLabel>
                      <FormControl>
                        <Input inputMode="numeric" placeholder="4" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="gradeMax"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Highest grade</FormLabel>
                      <FormControl>
                        <Input inputMode="numeric" placeholder="6" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="estimatedMinutes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Estimated minutes</FormLabel>
                      <FormControl>
                        <Input inputMode="numeric" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="credentialTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Credential title</FormLabel>
                      <FormControl>
                        <Input placeholder="Junior Robotics Builder" {...field} />
                      </FormControl>
                      <FormDescription>Leave blank to use the course title.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="sortOrder"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sort order</FormLabel>
                      <FormControl>
                        <Input inputMode="numeric" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="prerequisites"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prerequisites</FormLabel>
                    <FormControl>
                      <PrerequisitePicker value={field.value} onChange={field.onChange} options={prereqOptions} />
                    </FormControl>
                    <FormDescription>Courses a student must finish before enrolling in this one.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saveMutation.isPending} data-testid="button-save-course">
                  {saveMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : editing ? "Save changes" : "Create course"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Modules sheet ------------------------------------------------------------ */}
      <Sheet open={!!modulesFor} onOpenChange={(o) => { if (!o) setModulesFor(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          {modulesFor && <AdminModulesEditor key={modulesFor.id} course={modulesFor} onSaved={() => setModulesFor(null)} />}
        </SheetContent>
      </Sheet>

      {/* Enrollments -------------------------------------------------------------- */}
      <Dialog open={!!enrollmentsFor} onOpenChange={(o) => { if (!o) setEnrollmentsFor(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Enrolments</DialogTitle>
            <DialogDescription>{enrollmentsFor?.title}</DialogDescription>
          </DialogHeader>
          {enrollmentsFor && <EnrollmentsTable courseId={enrollmentsFor.id} />}
        </DialogContent>
      </Dialog>

      {/* Request changes ---------------------------------------------------------- */}
      <Dialog open={!!reviewFor} onOpenChange={(o) => { if (!o) { setReviewFor(null); setReviewNote(""); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Request changes</DialogTitle>
            <DialogDescription>
              {reviewFor?.title} goes back to the creator with your note. Be specific about what needs fixing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="review-note">Note for the creator</Label>
            <Textarea
              id="review-note"
              rows={5}
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              placeholder="Lesson 3 needs two more checks, and the summary block is missing."
              data-testid="input-review-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewFor(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => reviewFor && reviewMutation.mutate({ id: reviewFor.id, note: reviewNote.trim() })}
              disabled={!reviewNote.trim() || reviewMutation.isPending}
              data-testid="button-send-review-note"
            >
              {reviewMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : "Send back to creator"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate with AI --------------------------------------------------------- */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5" /> Generate a course with AI
            </DialogTitle>
            <DialogDescription>
              Claude drafts the course and its lessons. Everything lands as a draft for you to review. This takes a few
              minutes for a longer course.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input
                value={gen.title}
                onChange={(e) => setGen((g) => ({ ...g, title: e.target.value }))}
                placeholder="Build Your First Robot"
                data-testid="input-gen-title"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                rows={2}
                value={gen.description}
                onChange={(e) => setGen((g) => ({ ...g, description: e.target.value }))}
                placeholder="What the course covers and who it is for"
              />
            </div>
            <div>
              <Label>Interests</Label>
              <div className="pt-1">
                <TagPicker value={gen.interestTags} onChange={(next) => setGen((g) => ({ ...g, interestTags: next }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Level</Label>
                <Select value={gen.level} onValueChange={(v) => setGen((g) => ({ ...g, level: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COURSE_LEVELS.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Lessons</Label>
                <Select value={gen.lessons} onValueChange={(v) => setGen((g) => ({ ...g, lessons: v }))}>
                  <SelectTrigger data-testid="select-gen-lessons">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[3, 4, 5, 6, 7, 8].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} lessons
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Audience note (optional)</Label>
              <Input
                value={gen.audienceNote}
                onChange={(e) => setGen((g) => ({ ...g, audienceNote: e.target.value }))}
                placeholder="Grade 5 students who have never coded before"
              />
            </div>
            {genError && (
              <pre className="text-xs text-destructive whitespace-pre-wrap bg-destructive/10 rounded-md p-3 max-h-40 overflow-auto" data-testid="gen-error">
                {genError}
              </pre>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => { setGenError(null); generateMutation.mutate(); }}
              disabled={!gen.title.trim() || gen.interestTags.length === 0 || generateMutation.isPending}
              data-testid="button-generate-submit"
            >
              {generateMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Writing the course...</> : <><Wand2 className="w-4 h-4 mr-2" /> Generate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete ------------------------------------------------------------------- */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this course?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.title} will be removed along with its lesson list
              {deleteTarget?.enrollments ? ` and ${deleteTarget.enrollments} enrolment${deleteTarget.enrollments === 1 ? "" : "s"}` : ""}.
              The lessons themselves stay in the library. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              Delete course
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EnrollmentsTable({ courseId }: { courseId: string }) {
  const { data, isLoading, error } = useQuery<{ enrollments: AdminEnrollment[] }>({
    queryKey: ["/api/admin/courses", courseId, "enrollments"],
    queryFn: () => fetchJson<{ enrollments: AdminEnrollment[] }>(`/api/admin/courses/${courseId}/enrollments`),
  });

  if (isLoading) return <TableSkeleton rows={5} cols={4} />;
  if (error) {
    return (
      <div className="text-center py-8 space-y-1">
        <p className="font-medium text-destructive">Could not load enrolments</p>
        <p className="text-sm text-muted-foreground">{(error as Error)?.message || "Unknown error"}</p>
      </div>
    );
  }

  const rows = data?.enrollments ?? [];
  if (rows.length === 0) {
    return (
      <div className="text-center py-8 space-y-1">
        <p className="font-medium">Nobody has enrolled yet</p>
        <p className="text-sm text-muted-foreground">Publish the course and it will show up in recommendations.</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Student</TableHead>
          <TableHead>Grade / School</TableHead>
          <TableHead>Enrolled</TableHead>
          <TableHead>Progress</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((e) => (
          <TableRow key={e.userId}>
            <TableCell className="font-medium">
              {e.firstName} {e.lastName}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {e.grade ? `Grade ${e.grade}` : "No grade"}
              {e.school ? ` · ${e.school}` : ""}
            </TableCell>
            <TableCell className="text-sm">{e.enrolledAt ? new Date(e.enrolledAt).toLocaleDateString() : "Not recorded"}</TableCell>
            <TableCell className="text-sm">
              {e.completedAt ? (
                <Badge className="border-0 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">Completed</Badge>
              ) : (
                `${Math.round(e.progress ?? 0)}%`
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
